import clsx from 'clsx';
import type { RolloverPlan, RolloverPreview } from './studyYearApi';
import { formatShortDate } from './studyTermProgress';
import { countsSentence, wrapUpCounts } from './studyWrapUpDraft';

/**
 * The automatic preview summary on step 3 (study-my-year-prd.md §5.3). It shows the server's
 * dates and added subjects with the learner's choices. While a new preview is running it is marked as updating.
 */
export function WrapUpSummary({
  plan,
  preview,
  current,
  from,
  missingSubject,
  datesInvalid,
  previewFailed,
  onRetryPreview,
}: {
  plan: RolloverPlan;
  preview: RolloverPreview | undefined;
  current: boolean;
  from: string;
  missingSubject: string | null;
  datesInvalid: boolean;
  previewFailed: boolean;
  onRetryPreview: () => void;
}) {
  if (missingSubject)
    return (
      <p role="alert" className="mt-6 rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
        Keep {missingSubject} to bring its work with you.
      </p>
    );
  if (datesInvalid)
    return (
      <p role="alert" className="mt-6 rounded-2xl bg-wash-sunshine p-4 text-sm font-bold text-ink">
        Check your dates. Add a start and an end, with the end after the start.
      </p>
    );
  if (previewFailed)
    return (
      <div role="alert" className="mt-6 rounded-2xl bg-wash-coral p-4 text-sm text-ink">
        <p className="font-bold">We couldn&apos;t check your plan. Your choices are still here.</p>
        <button type="button" className="btn-pill-ghost mt-3" onClick={onRetryPreview}>
          Try again
        </button>
      </div>
    );
  if (!preview)
    return (
      <p role="status" className="mt-6 text-sm text-slate2">
        Updating…
      </p>
    );
  const { starts_on: startsOn, ends_on: endsOn } = preview.dates;
  const runs =
    startsOn && endsOn
      ? `${preview.to_term} runs ${formatShortDate(startsOn)} – ${formatShortDate(endsOn)}.`
      : `${preview.to_term} dates are not set yet.`;
  const counts = countsSentence(wrapUpCounts(plan));
  // The server's names for subjects added with "Add a subject" (PRD §5.3).
  const added = (preview.subjects ?? []).filter((subject) => subject.added);
  const addedSentence = added.length
    ? `New ${added.length === 1 ? 'subject' : 'subjects'}: ${added
        .map((subject) => subject.display_name)
        .join(', ')}.`
    : '';
  return (
    <div
      aria-live="polite"
      data-study-wrapup-summary=""
      aria-busy={!current}
      className={clsx(
        'mt-6 rounded-2xl bg-wash-sunshine p-4 text-sm text-ink',
        !current && 'opacity-60',
      )}
    >
      <p className="font-bold">
        {[runs, addedSentence, counts, `Your ${from} work stays saved.`]
          .filter(Boolean)
          .join(' ')}
      </p>
      {!current && (
        <p role="status" className="mt-1 text-xs font-semibold text-slate2">
          Updating…
        </p>
      )}
    </div>
  );
}
